// SessionRow.tsx — one session in the Sessions sidebar, on one line: a
// ButtonBadge whose lead says how recent the session is (a green dot while
// a turn runs, else "30s" / "3d" / "25 Aug"), followed by its title in the
// body color, with the session's own hue on the border only. The status line, the full title and the exact time
// are hover text; rename, unpin and delete are row actions revealed on
// hover or focus. A session with forks also carries an always-visible
// chevron and fork count that expands or collapses them.
//
// Renaming swaps the pill for an InlineText in the same row: a text field
// cannot live inside a <button>, and keeping it out of the pill is what
// lets the whole row be one real, keyboard-activatable button.
import React, { useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import { BsChevronDown, BsChevronRight, BsLockFill, BsPencil, BsTrash } from 'react-icons/bs';
import { BadgeDot, ButtonBadge, InlineText } from 'common';
import type { Session } from '../api';
import { formatRecency, sessionRecency } from './chatState';
import type { SessionActivity, SessionPhase } from './live/sessionLive';
import {
  rowAccent, rowAriaLabel, rowSubtitle, rowTooltip, titleToCommit, turnInFlight,
} from './sessionFormat';

/** The expand/collapse control of a session that has forks. */
export interface ForkToggle {
  /** Forks under this session, at any depth. */
  count: number;
  expanded: boolean;
  /** Id of the element holding the forks, for aria-controls. */
  groupId: string;
  onToggle: () => void;
}

export interface SessionRowProps {
  session: Session;
  isActive: boolean;
  /** An in-progress title edit, if any. */
  draft: string | undefined;
  /** What the session's harness says it is doing. */
  activity: SessionActivity;
  phase: SessionPhase | undefined;
  /** The clock the recency badge is printed against. */
  now: number;
  /** Select the row, or deselect it when it is already active. */
  onToggleSelect: () => void;
  onDraftChange: (value: string) => void;
  onDraftClear: () => void;
  onRename: (name: string) => void;
  onAutoTitle: () => void;
  onDelete: () => void;
  /** Present when the session has forks. */
  forks?: ForkToggle;
  /** The parent's title, when this session is a fork shown under it. */
  forkOf?: string;
}

const SessionRow: React.FC<SessionRowProps> = ({
  session: s, isActive, draft, activity, phase, now,
  onToggleSelect, onDraftChange, onDraftClear, onRename, onAutoTitle, onDelete,
  forks, forkOf,
}) => {
  const [editing, setEditing] = useState(false);
  // Set by Escape, so a blur that follows it can't commit the abandoned
  // draft through commitTitle's stale closure.
  const cancelled = useRef(false);
  const accent = rowAccent(s.id);
  // The same key this row was sorted by, so the badge can never disagree
  // with the row's position in the list.
  const stamp = sessionRecency(s);
  const recency = formatRecency(stamp, now);
  const working = turnInFlight(activity.status);
  const title = s.name || 'New session';
  const subtitle = rowSubtitle({ working, note: activity.note, phase, subtitle: s.subtitle });

  // Commit on blur/Enter. No-ops when nothing changed, so opening the
  // editor and leaving doesn't pin the auto-title.
  const commitTitle = () => {
    setEditing(false);
    onDraftClear();
    if (cancelled.current) return;
    const next = titleToCommit(draft, s.name);
    if (next !== null) onRename(next);
  };

  if (editing) {
    return (
      <div className="d-flex align-items-center">
        {/* Editing is what pins the name: the rename flips name_user_set,
            and the turn-end regen leaves pinned names alone from then on. */}
        <InlineText
          value={draft ?? s.name}
          minWidth={8}
          aria-label="Session title"
          autoFocus
          onChange={(e) => onDraftChange(e.currentTarget.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.currentTarget.blur();
            if (e.key === 'Escape') {
              cancelled.current = true;
              onDraftClear();
              setEditing(false);
            }
          }}
          onBlur={commitTitle}
          style={{ maxWidth: '100%', borderColor: accent }}
        />
      </div>
    );
  }

  return (
    <ButtonBadge
      badge={working
        ? <BadgeDot tone="success" label="Running" />
        : recency}
      active={isActive}
      accent={accent}
      // Clicking the active row deselects it — the way back to the empty
      // state, where the composer's model picker governs the next New
      // session instead of the open one.
      onClick={onToggleSelect}
      aria-label={rowAriaLabel(title, working, recency, forkOf)}
      title={rowTooltip({ title, subtitle, stamp, pinned: s.name_user_set })}
      trailing={forks && (
        <Button
          size="sm"
          variant="link"
          className="text-muted p-1 d-inline-flex align-items-center gap-1 small"
          onClick={forks.onToggle}
          aria-expanded={forks.expanded}
          aria-controls={forks.groupId}
          aria-label={`${forks.expanded ? 'Collapse' : 'Expand'} ${forks.count} fork${forks.count === 1 ? '' : 's'} of ${title}`}
          title={`${forks.count} fork${forks.count === 1 ? '' : 's'}`}
        >
          {forks.expanded ? <BsChevronDown /> : <BsChevronRight />}
          {forks.count}
        </Button>
      )}
      actions={
        <>
          {/* Shown only on a pinned name, and it's the way to unpin:
              auto-titling resumes on the next turn. */}
          {s.name_user_set && (
            <Button
              size="sm"
              variant="link"
              className="text-muted p-1"
              onClick={onAutoTitle}
              aria-label="Resume auto-titling"
              title="You named this session, so it won't be re-titled. Click to resume auto-titling."
            >
              <BsLockFill />
            </Button>
          )}
          <Button
            size="sm"
            variant="link"
            className="text-muted p-1"
            onClick={() => {
              cancelled.current = false;
              setEditing(true);
            }}
            aria-label="Rename session"
            title="Rename session"
          >
            <BsPencil />
          </Button>
          <Button
            size="sm"
            variant="link"
            className="text-muted p-1"
            onClick={onDelete}
            aria-label="Delete session"
            title="Delete session"
          >
            <BsTrash />
          </Button>
        </>
      }
    >
      {title}
    </ButtonBadge>
  );
};

export default SessionRow;
